import React, { useState } from 'react';
import { colors, spacing, typographyFlat, borderRadius, shadows } from '../../styles/tokens';
import { Select } from '../base/Select';
import { getInventoryStats } from '../../api/inventory';
import { finishFilterOptions } from '../../lib/cardVariantOptions';
import { inventoryLookupLabel, type InventoryLookupGame } from '../../lib/inventoryNav';

export interface InventoryFiltersState {
  condition: string;
  status: string;
  location: string;
  minQuantity: string;
  maxQuantity: string;
  language: string;
  cardNumber: string;
  sortBy: string;
  sortDir: string;
  productName: string;
  setName: string;
  finish: string;
  /** all | mtg | pokemon | lorcana */
  game: string;
  minCost: string;
  maxCost: string;
  updatedAfter: string;
  updatedBefore: string;
}

interface InventoryFiltersProps {
  filters: InventoryFiltersState;
  /** Route game. List and stats stay on this game; the game dropdown is the section nav. */
  scopedGame: InventoryLookupGame;
  filterOptions: {
    sets: {
      total: number;
      options: Array<{
        name: string;
        copies: number;
      }>;
    };
    locations: {
      options: string[];
    };
  };
  onChange: (filters: InventoryFiltersState) => void;
  onReset: () => void;
}

const CONDITIONS = [
  { value: 'All', label: 'Any Condition' },
  { value: 'NM', label: 'Near Mint (NM)' },
  { value: 'LP', label: 'Lightly Played (LP)' },
  { value: 'MP', label: 'Moderately Played (MP)' },
  { value: 'HP', label: 'Heavily Played (HP)' },
  { value: 'Damaged', label: 'Damaged' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'Any Status' },
  { value: 'available', label: 'In Stock' },
  { value: 'sold', label: 'Sold' },
  { value: 'held', label: 'Held' },
];

const LANGUAGES = [
  { value: 'All', label: 'All' },
  { value: 'EN', label: 'English' },
  { value: 'JA', label: 'Japanese' },
  { value: 'FR', label: 'French' },
  { value: 'DE', label: 'German' },
  { value: 'ES', label: 'Spanish' },
  { value: 'IT', label: 'Italian' },
  { value: 'PT', label: 'Portuguese' },
  { value: 'KO', label: 'Korean' },
  { value: 'ZHS', label: 'Chinese Simplified' },
  { value: 'ZHT', label: 'Chinese Traditional' },
  { value: 'RU', label: 'Russian' },
];

const SORT_OPTIONS = [
  { value: 'name', label: 'Product Name' },
  { value: 'condition', label: 'Condition' },
  { value: 'acquired_at', label: 'Updated Date' },
  { value: 'acquisition_cost', label: 'Cost Basis' },
  { value: 'quantity', label: 'Quantity' },
];

export const InventoryFilters: React.FC<InventoryFiltersProps> = ({
  filters,
  scopedGame,
  filterOptions,
  onChange,
  onReset,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [stats, setStats] = useState<{
    min_quantity: number;
    max_quantity: number;
    min_cost: number;
    max_cost: number;
  } | null>(null);

  const finishOptions = finishFilterOptions(scopedGame);
  const finishChoices = finishOptions.some((option) => option.value === filters.finish)
    ? finishOptions
    : [...finishOptions, { value: filters.finish, label: filters.finish }];

  // Quantity and cost ranges follow the route game.
  React.useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const next = await getInventoryStats({ game: scopedGame });
        if (!cancelled) setStats(next);
      } catch (error) {
        void error;
      }
    };
    fetchStats();
    return () => {
      cancelled = true;
    };
  }, [scopedGame]);

  const handleChange = (field: keyof InventoryFiltersState, value: string) => {
    onChange({
      ...filters,
      [field]: value,
    });
  };

  // Get list of active filters (excluding 'all', 'All', and sort fields)
  const getActiveFilters = () => {
    const active: Array<{ label: string; value: string; field: keyof InventoryFiltersState }> = [];
    
    if (filters.productName) active.push({ label: 'Name', value: filters.productName, field: 'productName' });
    if (filters.setName) active.push({ label: 'Set', value: filters.setName, field: 'setName' });
    if (filters.condition && filters.condition !== 'All') active.push({ label: 'Condition', value: filters.condition, field: 'condition' });
    if (filters.status && filters.status !== 'all') active.push({ label: 'Status', value: STATUS_OPTIONS.find((o) => o.value === filters.status)?.label || filters.status, field: 'status' });
    if (filters.finish && filters.finish !== 'all') active.push({ label: 'Finish', value: finishChoices.find(o => o.value === filters.finish)?.label || filters.finish, field: 'finish' });
    if (filters.language && filters.language !== 'All') active.push({ label: 'Language', value: filters.language, field: 'language' });
    if (filters.location) active.push({ label: 'Location', value: filters.location, field: 'location' });
    if (filters.cardNumber) active.push({ label: 'Card #', value: filters.cardNumber, field: 'cardNumber' });
    if (filters.minQuantity) active.push({ label: 'Min Qty', value: filters.minQuantity, field: 'minQuantity' });
    if (filters.maxQuantity) active.push({ label: 'Max Qty', value: filters.maxQuantity, field: 'maxQuantity' });
    if (filters.minCost) active.push({ label: 'Min Cost', value: `$${filters.minCost}`, field: 'minCost' });
    if (filters.maxCost) active.push({ label: 'Max Cost', value: `$${filters.maxCost}`, field: 'maxCost' });
    if (filters.updatedAfter) active.push({ label: 'After', value: filters.updatedAfter, field: 'updatedAfter' });
    if (filters.updatedBefore) active.push({ label: 'Before', value: filters.updatedBefore, field: 'updatedBefore' });
    
    return active;
  };

  const activeFilters = getActiveFilters();
  const activeFilterCount = activeFilters.length;
  const setSelectOptions = [
    ...(filters.setName && !filterOptions.sets.options.some((setOption) => setOption.name === filters.setName)
      ? [{ value: filters.setName, label: filters.setName }]
      : []),
    ...filterOptions.sets.options.map((setOption) => ({
      value: setOption.name,
      label: `${setOption.name} (${setOption.copies})`,
    })),
  ];
  const locationSelectOptions = [
    ...(filters.location && !filterOptions.locations.options.includes(filters.location)
      ? [{ value: filters.location, label: filters.location }]
      : []),
    ...filterOptions.locations.options.map((locationOption) => ({
      value: locationOption,
      label: locationOption,
    })),
  ];
  const hasAdvancedFilters = filters.location || filters.cardNumber || filters.minQuantity || 
                              filters.maxQuantity || filters.minCost || filters.maxCost || 
                              filters.updatedAfter || filters.updatedBefore || filters.language !== 'All';

  const removeFilter = (field: keyof InventoryFiltersState) => {
      const reset =
        field === 'condition' || field === 'language'
          ? 'All'
          : field === 'finish' || field === 'status' || field === 'game'
            ? 'all'
            : '';
      handleChange(field, reset);
  };

  return (
    <div style={styles.container}>
      {/* ── Search Row ── */}
      <div style={styles.searchRow}>
        <div style={styles.searchInputWrapper}>
          <span style={styles.searchIconGlyph} aria-hidden="true">🔍</span>
          <input
            type="text"
            placeholder="Search by product name..."
            value={filters.productName}
            onChange={(e) => handleChange('productName', e.target.value)}
            style={styles.searchInput}
          />
          {filters.productName && (
            <button
              type="button"
              onClick={() => handleChange('productName', '')}
              style={styles.searchClearBtn}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* ── Filter + Sort Row ── */}
      <div style={styles.filterRow}>
        <select
          value={filters.condition}
          onChange={(e) => handleChange('condition', e.target.value)}
          style={{ ...styles.filterPill, ...(filters.condition !== 'All' ? styles.filterPillActive : {}) }}
        >
          {CONDITIONS.map((cond) => (
            <option key={cond.value} value={cond.value}>{cond.label}</option>
          ))}
        </select>

        <select
          value={filters.status}
          onChange={(e) => handleChange('status', e.target.value)}
          style={{ ...styles.filterPill, ...(filters.status !== 'all' ? styles.filterPillActive : {}) }}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        <select
          value={filters.finish}
          onChange={(e) => handleChange('finish', e.target.value)}
          style={{ ...styles.filterPill, ...(filters.finish !== 'all' ? styles.filterPillActive : {}) }}
          aria-label={`${inventoryLookupLabel(scopedGame)} printing`}
        >
          {finishChoices.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <div style={{ flex: 1 }} />

        <select
          value={`${filters.sortBy}:${filters.sortDir}`}
          onChange={(e) => {
            const [newSortBy, newSortDir] = e.target.value.split(':');
            onChange({ ...filters, sortBy: newSortBy, sortDir: newSortDir });
          }}
          style={styles.sortSelect}
        >
          <optgroup label="Sort By">
            {SORT_OPTIONS.map((opt) => (
              <React.Fragment key={opt.value}>
                <option value={`${opt.value}:asc`}>{opt.label} ↑</option>
                <option value={`${opt.value}:desc`}>{opt.label} ↓</option>
              </React.Fragment>
            ))}
          </optgroup>
        </select>

        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          style={{
            ...styles.advancedToggle,
            ...(hasAdvancedFilters ? styles.advancedToggleActive : {}),
          }}
        >
          {hasAdvancedFilters ? `Filters (${activeFilterCount})` : 'Filters'}{' '}
          {showAdvanced ? '▲' : '▼'}
        </button>
      </div>

      {/* ── Active Filter Chips ── */}
      {activeFilters.length > 0 && (
        <div style={styles.chipsContainer}>
          {activeFilters.map((filter) => (
            <div key={filter.field} style={styles.chip}>
              <span style={styles.chipLabel}>{filter.label}:</span>
              <span style={styles.chipValue}>{filter.value}</span>
              <button
                type="button"
                onClick={() => removeFilter(filter.field)}
                style={styles.chipRemove}
                aria-label={`Remove ${filter.label} filter`}
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={onReset}
            style={styles.clearAllButton}
          >
            Clear all
          </button>
        </div>
      )}

      {/* Advanced Filters (Collapsible) */}
      {showAdvanced && (
        <div style={styles.advancedSection}>
          <div style={styles.advancedTitle}>Advanced filters</div>
          <div style={styles.advancedGrid}>
            {/* Set Name */}
            <div style={styles.field}>
              <label style={styles.label}>Set Name</label>
              <Select
                options={setSelectOptions}
                value={filters.setName || undefined}
                onChange={(value) => handleChange('setName', value)}
                placeholder={`Select Set (${filterOptions.sets.total})`}
                searchable
                fullWidth
              />
            </div>

            {/* Location */}
            <div style={styles.field}>
              <label style={styles.label}>Location</label>
              <Select
                options={locationSelectOptions}
                value={filters.location || undefined}
                onChange={(value) => handleChange('location', value)}
                placeholder="Select Location"
                searchable
                fullWidth
              />
            </div>

            {/* Card Number */}
            <div style={styles.field}>
              <label style={styles.label}>Card Number</label>
              <input
                type="text"
                placeholder="e.g., 123"
                value={filters.cardNumber}
                onChange={(e) => handleChange('cardNumber', e.target.value)}
                style={styles.input}
              />
            </div>

            {/* Language */}
            <div style={styles.field}>
              <label style={styles.label}>Language</label>
              <select
                value={filters.language}
                onChange={(e) => handleChange('language', e.target.value)}
                style={styles.select}
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang.value} value={lang.value}>{lang.label}</option>
                ))}
              </select>
            </div>

            {/* Quantity Range */}
            <div style={styles.field}>
              <label style={styles.label}>Quantity</label>
              <div style={styles.rangeContainer}>
                <input
                  type="number"
                  placeholder={stats ? String(stats.min_quantity) : 'Min'}
                  min="0"
                  value={filters.minQuantity}
                  onChange={(e) => handleChange('minQuantity', e.target.value)}
                  style={styles.rangeInput}
                />
                <span style={styles.rangeDash}>—</span>
                <input
                  type="number"
                  placeholder={stats ? String(stats.max_quantity) : 'Max'}
                  min="0"
                  value={filters.maxQuantity}
                  onChange={(e) => handleChange('maxQuantity', e.target.value)}
                  style={styles.rangeInput}
                />
              </div>
            </div>

            {/* Cost Range */}
            <div style={styles.field}>
              <label style={styles.label}>Cost ($)</label>
              <div style={styles.rangeContainer}>
                <input
                  type="number"
                  placeholder={stats ? String(stats.min_cost) : 'Min'}
                  min="0"
                  step="0.01"
                  value={filters.minCost}
                  onChange={(e) => handleChange('minCost', e.target.value)}
                  style={styles.rangeInput}
                />
                <span style={styles.rangeDash}>—</span>
                <input
                  type="number"
                  placeholder={stats ? String(stats.max_cost) : 'Max'}
                  min="0"
                  step="0.01"
                  value={filters.maxCost}
                  onChange={(e) => handleChange('maxCost', e.target.value)}
                  style={styles.rangeInput}
                />
              </div>
            </div>

            {/* Date Range */}
            <div style={styles.field}>
              <label style={styles.label}>Updated Date</label>
              <div style={styles.rangeContainer}>
                <input
                  type="date"
                  value={filters.updatedAfter}
                  onChange={(e) => handleChange('updatedAfter', e.target.value)}
                  style={styles.rangeInput}
                />
                <span style={styles.rangeDash}>—</span>
                <input
                  type="date"
                  value={filters.updatedBefore}
                  onChange={(e) => handleChange('updatedBefore', e.target.value)}
                  style={styles.rangeInput}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DROPDOWN_ARROW_GRAY = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%239e9e9e' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")`;
const DROPDOWN_ARROW_BLUE = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%231976d2' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")`;
const DROPDOWN_ARROW_DARK = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23616161' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")`;

const styles = {
  // ── Container
  container: {
    backgroundColor: colors.surface,
    padding: `${spacing.sm} ${spacing.md}`,
    borderRadius: borderRadius.lg,
    marginBottom: spacing.lg,
    border: `1px solid ${colors.border}`,
    boxShadow: shadows.sm,
  },
  // ── Search Row
  searchRow: {
    marginBottom: spacing.sm,
  },
  searchInputWrapper: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
  },
  searchIconGlyph: {
    position: 'absolute' as const,
    left: spacing.sm,
    fontSize: '15px',
    color: colors.textSecondary,
    pointerEvents: 'none' as const,
    userSelect: 'none' as const,
  },
  searchInput: {
    width: '100%',
    boxSizing: 'border-box' as const,
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
    paddingLeft: `calc(${spacing.sm} + 22px)`,
    paddingRight: `calc(${spacing.md} + 18px)`,
    fontSize: typographyFlat.fontSize.md,
    border: `1.5px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.background,
    color: colors.text,
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  } as React.CSSProperties,
  searchClearBtn: {
    position: 'absolute' as const,
    right: spacing.xs,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: colors.textSecondary,
    fontSize: '20px',
    lineHeight: '1',
    padding: `0 ${spacing.xs}`,
    display: 'flex',
    alignItems: 'center',
  },
  // ── Filter + Sort Row
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    flexWrap: 'wrap' as const,
  },
  filterPill: {
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
    padding: `6px ${spacing.md}`,
    paddingRight: `calc(${spacing.md} + 14px)`,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.textSecondary,
    backgroundColor: colors.gray100,
    border: `1px solid transparent`,
    borderRadius: borderRadius.md,
    cursor: 'pointer',
    outline: 'none',
    backgroundImage: DROPDOWN_ARROW_GRAY,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    transition: 'all 0.15s',
  } as React.CSSProperties,
  filterPillActive: {
    backgroundColor: `${colors.primaryLight}22`,
    color: colors.primary,
    border: `1px solid ${colors.primaryLight}`,
    fontWeight: typographyFlat.fontWeight.semibold,
    backgroundImage: DROPDOWN_ARROW_BLUE,
  } as React.CSSProperties,
  sortSelect: {
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
    padding: `6px ${spacing.md}`,
    paddingRight: `calc(${spacing.md} + 14px)`,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.text,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    cursor: 'pointer',
    outline: 'none',
    backgroundImage: DROPDOWN_ARROW_DARK,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    transition: 'border-color 0.15s',
  } as React.CSSProperties,
  advancedToggle: {
    padding: `6px ${spacing.md}`,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.textSecondary,
    backgroundColor: 'transparent',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    transition: 'all 0.15s',
  },
  advancedToggleActive: {
    color: colors.primary,
    borderColor: colors.primary,
    backgroundColor: `${colors.primaryLight}15`,
  },
  // ── Active filter chips
  chipsContainer: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: spacing.xs,
    paddingTop: spacing.sm,
    alignItems: 'center',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing.xs,
    padding: `${spacing.xs} ${spacing.sm}`,
    backgroundColor: `${colors.primaryLight}20`,
    color: colors.primary,
    borderRadius: borderRadius.full,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.medium,
    border: `1px solid ${colors.primaryLight}55`,
  },
  chipLabel: {
    opacity: 0.8,
  },
  chipValue: {
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  chipRemove: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    marginLeft: spacing.xs,
    padding: 0,
    border: 'none',
    background: 'none',
    color: colors.primary,
    fontSize: '18px',
    lineHeight: '1',
    cursor: 'pointer',
    opacity: 0.7,
  } as React.CSSProperties,
  clearAllButton: {
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.error,
    backgroundColor: 'transparent',
    border: `1px solid ${colors.error}55`,
    borderRadius: borderRadius.full,
    cursor: 'pointer',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,
  // ── Advanced Panel
  advancedSection: {
    marginTop: spacing.sm,
    paddingTop: spacing.md,
    borderTop: `1px solid ${colors.border}`,
  },
  advancedTitle: {
    marginBottom: spacing.sm,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.textSecondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  advancedGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: spacing.md,
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.xs,
  },
  label: {
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.text,
  },
  input: {
    padding: spacing.sm,
    fontSize: typographyFlat.fontSize.sm,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    outline: 'none',
    color: colors.text,
    transition: 'border-color 0.2s ease',
  } as React.CSSProperties,
  select: {
    padding: spacing.sm,
    fontSize: typographyFlat.fontSize.sm,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    outline: 'none',
    backgroundColor: colors.surface,
    color: colors.text,
    cursor: 'pointer',
    transition: 'border-color 0.2s ease',
  } as React.CSSProperties,
  rangeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
  },
  rangeInput: {
    flex: 1,
    padding: spacing.sm,
    fontSize: typographyFlat.fontSize.sm,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    outline: 'none',
    backgroundColor: colors.surface,
    color: colors.text,
    transition: 'border-color 0.2s ease',
    minWidth: '60px',
  } as React.CSSProperties,
  rangeDash: {
    color: colors.textSecondary,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.medium,
    userSelect: 'none' as const,
    flexShrink: 0,
  },
};
